import { GlassSkeleton } from "@/components/ui/glass-skeleton";
import { Card } from "@/components/ui/card";
import { LoadingAnnouncer } from "@/components/ui/loading-announcer";

/**
 * EnhancedHomeTabSkeleton - EXACT HEIGHT MATCHING
 * 
 * Heights match actual HomeTab component dimensions:
 * - Energy card (md:col-span-2): 180px
 * - AI Insights card (md:col-span-3): 180px  
 * - Quick action cards: 140px each
 * - Top symptoms card: 240px
 * - Chart card: 380px
 * - Appointments card: 120px
 * - Streak card: 100px
 * 
 * IMPORTANT: NO backdrop-blur on any element - this skeleton is shown during
 * tab transitions and any blur causes a 2-second "settling" effect on Android PWA.
 */
export const EnhancedHomeTabSkeleton = () => {
  return (
    <div className="space-y-3 sm:space-y-5 animate-fade-in" style={{ minHeight: '1200px' }}>
      {/* Accessible loading announcement */}
      <LoadingAnnouncer isLoading={true} loadingMessage="Loading your health dashboard..." />
      
      {/* Beta Banner Skeleton - height: 80px */}
      <Card 
        className="p-4 bg-gradient-to-r from-primary/5 to-purple-500/5 border-primary/20"
        style={{ height: '80px' }}
      >
        <div className="flex items-start gap-3">
          <GlassSkeleton className="h-5 w-5 rounded" intensity="medium" />
          <div className="flex-1 space-y-2">
            <GlassSkeleton className="h-5 w-48" intensity="medium" />
            <GlassSkeleton className="h-4 w-full" intensity="light" />
          </div>
        </div>
      </Card>

      {/* Energy Level & AI Insights Row - NO backdrop-blur */}
      <div className="grid md:grid-cols-5 gap-3 sm:gap-5">
        {/* Energy Card - height: 180px */}
        <div className="md:col-span-2">
          <Card 
            className="p-6 bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20"
            style={{ height: '180px' }}
          >
            <div className="space-y-4 h-full">
              <div className="flex items-center gap-3">
                <GlassSkeleton className="h-16 w-16 rounded-full" intensity="medium" />
                <div className="flex-1 space-y-2">
                  <GlassSkeleton className="h-6 w-32" intensity="medium" />
                  <GlassSkeleton className="h-4 w-24" intensity="light" />
                </div>
              </div>
              <GlassSkeleton className="h-16 w-full rounded-lg" intensity="light" />
            </div>
          </Card>
        </div>
        
        {/* AI Insights Card - height: 180px */}
        <div className="md:col-span-3">
          <Card 
            className="p-6 bg-card border-border/20"
            style={{ height: '180px' }}
          >
            <div className="space-y-4 h-full">
              <GlassSkeleton className="h-6 w-48" intensity="medium" />
              <GlassSkeleton className="h-24 w-full rounded-lg" intensity="light" />
            </div>
          </Card>
        </div>
      </div>

      {/* Quick Action Cards Grid - height: 140px each */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {[1, 2, 3, 4].map((i) => (
          <Card 
            key={i} 
            className="p-4 bg-card border-border/20 overflow-hidden relative"
            style={{ height: '140px' }}
          >
            {/* Shimmer overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-[shimmer_2s_infinite]" />
            
            <div className="relative space-y-3 h-full flex flex-col">
              <GlassSkeleton className="h-12 w-12 rounded-xl" intensity="medium" />
              <div className="space-y-2 flex-1">
                <GlassSkeleton className="h-5 w-32" intensity="medium" />
                <GlassSkeleton className="h-4 w-full" intensity="light" />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Top Symptoms Card - height: 240px */}
      <Card 
        className="p-6 bg-card border-border/20"
        style={{ height: '240px' }}
      >
        <div className="space-y-4 h-full">
          <div className="flex items-center gap-3">
            <GlassSkeleton className="h-10 w-10 rounded-xl" intensity="medium" />
            <GlassSkeleton className="h-6 w-48" intensity="medium" />
          </div>
          <div className="space-y-3 flex-1">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-3" style={{ height: '40px' }}>
                <GlassSkeleton className="h-6 w-6 rounded-full" intensity="medium" />
                <GlassSkeleton className="h-4 flex-1" intensity="light" />
                <GlassSkeleton className="h-4 w-16" intensity="light" />
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Symptom Trend Chart - height: 380px */}
      <Card 
        className="p-6 bg-card border-border/20 border-2 border-[hsl(var(--chart-accent))]/20"
        style={{ height: '380px' }}
      >
        <div className="space-y-4 h-full flex flex-col">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <GlassSkeleton className="h-10 w-10 rounded-xl" intensity="medium" />
              <div className="space-y-2">
                <GlassSkeleton className="h-5 w-40" intensity="medium" />
                <GlassSkeleton className="h-4 w-32" intensity="light" />
              </div>
            </div>
          </div>
          <GlassSkeleton className="flex-1 w-full rounded-lg" intensity="light" />
        </div>
      </Card>

      {/* Appointments Card - height: 120px */}
      <Card 
        className="p-6 bg-card border-border/20"
        style={{ height: '120px' }}
      >
        <div className="space-y-4 h-full">
          <div className="flex items-center gap-3">
            <GlassSkeleton className="h-12 w-12 rounded-xl" intensity="medium" />
            <div className="flex-1 space-y-2">
              <GlassSkeleton className="h-5 w-32" intensity="medium" />
              <GlassSkeleton className="h-6 w-24" intensity="medium" />
            </div>
          </div>
        </div>
      </Card>

      {/* Streak Card - height: 100px */}
      <Card 
        className="p-6 bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20"
        style={{ height: '100px' }}
      >
        <div className="flex items-center gap-3 h-full">
          <GlassSkeleton className="h-12 w-12 rounded-full" intensity="medium" />
          <div className="flex-1 space-y-2">
            <GlassSkeleton className="h-6 w-40" intensity="medium" />
            <GlassSkeleton className="h-4 w-56" intensity="light" />
          </div>
        </div>
      </Card>
    </div>
  );
};
